import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useShellTabs } from "../../stores/shell-tabs";

const SCROLL_STEP = 180;

export function TabBar() {
  const navigate = useNavigate();
  const tabs = useShellTabs((s) => s.tabs);
  const activeId = useShellTabs((s) => s.activeId);
  const setActiveTab = useShellTabs((s) => s.setActiveTab);
  const closeTab = useShellTabs((s) => s.closeTab);
  const openInNewTab = useShellTabs((s) => s.openInNewTab);

  const scrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 1);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      observer.disconnect();
    };
  }, [tabs, updateScrollState]);

  useEffect(() => {
    const node = tabRefs.current.get(activeId);
    node?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }, [activeId, tabs]);

  const scrollTabs = (direction: -1 | 1) => {
    scrollRef.current?.scrollBy({ left: direction * SCROLL_STEP, behavior: "smooth" });
  };

  return (
    <div
      className="flex min-w-0 flex-1 items-center gap-0 bg-surface-2"
      style={{ height: "var(--tabbar-height)" }}
    >
      <button
        type="button"
        aria-label="Scroll tabs left"
        disabled={!canScrollLeft}
        onClick={() => scrollTabs(-1)}
        className="flex h-full w-9 shrink-0 items-center justify-center border-r border-line text-muted transition-colors hover:bg-hover hover:text-foreground disabled:pointer-events-none disabled:opacity-25"
      >
        <ChevronLeft size={18} />
      </button>

      <div
        ref={scrollRef}
        className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map((tab) => {
          const active = tab.id === activeId;
          return (
            <div
              key={tab.id}
              ref={(node) => {
                if (node) tabRefs.current.set(tab.id, node);
                else tabRefs.current.delete(tab.id);
              }}
              className={`group flex max-w-[220px] shrink-0 items-center gap-1 rounded-t-lg border border-b-0 px-3 py-1.5 text-xs transition-colors ${
                active
                  ? "border-brand-pink/35 bg-surface text-foreground"
                  : "border-transparent bg-transparent text-muted hover:bg-hover hover:text-foreground"
              }`}
            >
              <button
                type="button"
                className="min-w-0 flex-1 truncate text-left"
                onClick={() => {
                  const path = setActiveTab(tab.id);
                  if (path) navigate(path);
                }}
              >
                {tab.title}
              </button>
              {tabs.length > 1 && (
                <button
                  type="button"
                  aria-label={`Close ${tab.title}`}
                  className={`shrink-0 rounded p-0.5 transition-colors hover:bg-hover hover:text-foreground ${
                    active ? "text-foreground" : "text-muted-2 group-hover:text-muted"
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();
                    const path = closeTab(tab.id);
                    if (path) navigate(path);
                  }}
                >
                  <X size={12} strokeWidth={2} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        aria-label="Scroll tabs right"
        disabled={!canScrollRight}
        onClick={() => scrollTabs(1)}
        className="flex h-full w-9 shrink-0 items-center justify-center border-l border-line text-muted transition-colors hover:bg-hover hover:text-foreground disabled:pointer-events-none disabled:opacity-25"
      >
        <ChevronRight size={18} />
      </button>

      <button
        type="button"
        aria-label="Open new tab"
        className="flex h-full w-10 shrink-0 items-center justify-center border-l border-line text-muted transition-colors hover:bg-hover hover:text-foreground"
        onClick={() => {
          const path = openInNewTab("/");
          navigate(path);
        }}
      >
        <Plus size={16} />
      </button>
    </div>
  );
}
